/**
 * RFC 9457 problem details (§15.1): machine-readable `type`, localized `detail`, support reference and field
 * pointers. Cross-tenant probes answer `not_found`, never `forbidden`.
 */
import { z } from 'zod';
export const PROBLEM_TYPE_BASE = 'https://windrop.dev/problems/';
export const PROBLEM_CODES = {
    validation: { status: 400, title: 'Request validation failed' },
    idempotency_conflict: { status: 409, title: 'Idempotency key reused with a different request' },
    idempotency_in_progress: { status: 409, title: 'A request with this idempotency key is still processing' },
    lottery_not_open: { status: 409, title: 'This lottery is not accepting entries' },
    lottery_paused: { status: 409, title: 'This lottery is paused' },
    entry_cap_reached: { status: 409, title: 'Entry limit reached' },
    bonus_cap_reached: { status: 409, title: 'Bonus entry limit reached' },
    ineligible: { status: 422, title: 'Not eligible for this lottery' },
    payment_required: { status: 402, title: 'Payment is required to complete this entry' },
    payment_pending: { status: 409, title: 'Payment is still being confirmed' },
    payment_failed: { status: 409, title: 'Payment was not completed' },
    payment_terminal: { status: 409, title: 'Payment is in a terminal state' },
    webhook_unauthenticated: { status: 401, title: 'Webhook signature could not be verified' },
    webhook_replayed: { status: 409, title: 'Webhook event already processed' },
    hold_expired: { status: 409, title: 'The reservation time ended' },
    hold_conflict: { status: 409, title: 'Another reservation is active for this win' },
    variant_unavailable: { status: 409, title: 'That option is no longer available' },
    value_cap_exceeded: { status: 422, title: 'Selection exceeds the published value cap' },
    brand_not_approved: { status: 422, title: 'Brand is not on the approved list' },
    claim_token_invalid: { status: 401, title: 'Claim link is invalid or already used' },
    claim_session_required: { status: 401, title: 'A claim session is required' },
    claim_window_closed: { status: 409, title: 'The claim window has closed' },
    claim_already_submitted: { status: 409, title: 'This claim has already been submitted' },
    verification_not_verified: { status: 409, title: 'Claim cannot be approved before verification succeeds' },
    win_not_found: { status: 404, title: 'Win ID not recognized' },
    win_link_invalid: { status: 400, title: 'Win link signature is invalid or expired' },
    rate_limited: { status: 429, title: 'Too many requests' },
    not_found: { status: 404, title: 'Not found' },
    unauthenticated: { status: 401, title: 'Authentication required' },
    forbidden: { status: 403, title: 'Permission denied' },
    step_up_required: { status: 403, title: 'Step-up authentication required' },
    dual_control_required: { status: 403, title: 'A second approver is required for this action' },
    separation_of_duties: { status: 403, title: 'The same person cannot perform both steps' },
    forbidden_transition: { status: 409, title: 'State transition is not allowed' },
    config_locked: { status: 409, title: 'Configuration is locked after the first entry' },
    config_invalid: { status: 422, title: 'Lottery configuration is not publishable' },
    version_conflict: { status: 409, title: 'The resource changed since you loaded it' },
    tenant_mismatch: { status: 404, title: 'Not found' },
    provider_unavailable: { status: 503, title: 'A provider service is temporarily unavailable' },
    provider_capability_missing: { status: 501, title: 'Provider does not support this capability' },
    reconciliation_exception: { status: 409, title: 'Payment is under reconciliation review' },
    stock_exhausted: { status: 409, title: 'Prize stock is exhausted' },
    draw_not_ready: { status: 409, title: 'Pre-draw checks have not passed' },
    draw_in_progress: { status: 409, title: 'A draw is already in progress' },
    redraw_requires_reason: { status: 422, title: 'A redraw needs a reason and a new seed commitment' },
    export_expired: { status: 410, title: 'Export link has expired' },
    consent_required: { status: 422, title: 'Required consent is missing' },
    unsupported_media: { status: 415, title: 'Unsupported media type' },
    payload_too_large: { status: 413, title: 'Payload too large' },
    internal: { status: 500, title: 'Something went wrong on our side' },
};
export const ProblemCode = z.enum(Object.keys(PROBLEM_CODES));
export const problemType = (code) => `${PROBLEM_TYPE_BASE}${code}`;
export const problemCodeFromType = (type) => {
    if (!type.startsWith(PROBLEM_TYPE_BASE))
        return undefined;
    const code = type.slice(PROBLEM_TYPE_BASE.length);
    return ProblemCode.safeParse(code).success ? code : undefined;
};
/** One field-level issue; `pointer` is an RFC 6901 JSON pointer into the request body (or `#/headers/...`). */
export const FieldError = z.object({
    pointer: z.string().regex(/^(?:#)?(?:\/[^/]*)*$/),
    message: z.string(),
    code: z.string().max(64),
});
export const ProblemDetails = z.object({
    type: z.string().url().startsWith(PROBLEM_TYPE_BASE),
    title: z.string(),
    status: z.number().int().min(100).max(599),
    detail: z.string().optional(),
    instance: z.string().optional(),
    /** Short reference participants can quote to support; never a token or an internal id. */
    supportReference: z.string().max(32).optional(),
    errors: z.array(FieldError).optional(),
    /** ISO instant after which the client may retry (rate limiting, pending payments). */
    retryAfter: z.string().optional(),
});
export const PROBLEM_CONTENT_TYPE = 'application/problem+json';
/** Build a problem object for a catalogue code; the API's `problem()` helper wraps this with localization. */
export const makeProblem = (code, extras = {}) => ({
    type: problemType(code),
    title: PROBLEM_CODES[code].title,
    status: PROBLEM_CODES[code].status,
    ...extras,
});
//# sourceMappingURL=errors.js.map